import type { SelectOptionWithColor } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import debounce from 'lodash-es/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { TagRead } from 'src/common/external/bambi-api/bambiApi';
import { useTagsListQuery } from 'src/common/external/bambi-api/bambiApi';

export const TAG_SEARCH_DEBOUNCE_MS = 300;

const NO_TAGS: TagRead[] = [];

export function useTagOptions({
  excludeTagIds,
  skip,
}: {
  excludeTagIds?: string[];
  skip?: boolean;
} = {}) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const debouncedSetQuery = useMemo(
    () => debounce(setDebouncedQuery, TAG_SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const { data } = useTagsListQuery({ search: debouncedQuery }, { skip });

  const tags = useMemo(() => data?.results ?? NO_TAGS, [data]);

  const options: SelectOptionWithColor[] = useMemo(
    () =>
      tags
        .filter((tag) => !excludeTagIds?.includes(tag.id))
        .map((tag) => ({
          label: tag.tag_name,
          value: tag.id,
          color: tag.color_hex || '',
        })),
    [tags, excludeTagIds]
  );

  const handleSetQuery = useCallback(
    (value: string) => {
      setQuery(value);
      debouncedSetQuery(value);
    },
    [debouncedSetQuery]
  );

  const resetQuery = useCallback(() => {
    debouncedSetQuery.cancel();
    setQuery('');
    setDebouncedQuery('');
  }, [debouncedSetQuery]);

  return {
    options,
    query,
    setQuery: handleSetQuery,
    resetQuery,
  };
}
